import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { APIBlock, APIBlockItem, ComponentData } from "@companion-core/shared/app/Interfaces/API";
import {
  isBlockItemsContainAQuery,
  TYPE_CARD,
} from "@companion-core/shared/app/Utils/componentHelper";

export interface BlockStoreContent extends APIBlock {
  blockItems: APIBlockItem[];
  numberOfColoredItems: number;
  isLoaded: boolean;
  colorIndex?: number;
}

interface DynamicPageState {
  pageSlug: string | null;
  isLoaded: boolean;
  blocksBySlug: Record<string, BlockStoreContent>;
  scrollIndex: Record<string, number>;
}

interface DynamicPageActions {
  setPage: (pageSlug: string, apiBlocks: APIBlock[]) => void;
  setBlockContent: (blockSlug: string, apiBlockItems: APIBlockItem[]) => void;
  setScrollIndex: (scrollIndex: Record<string, number>) => void;
  setBlockColorIndex: (blockSlug: string, colorIndex: number) => void;
  setBlockItemComponents: (
    blockSlug: string,
    blockItemId: string,
    components: ComponentData[]
  ) => void;
  setBlockLoading: (blockSlug: string) => void;
  resetPage: () => void;
}

export const initialState: DynamicPageState = {
  pageSlug: null,
  isLoaded: false,
  blocksBySlug: {},
  scrollIndex: {},
};

export const useDynamicPageStore = createWithEqualityFn<DynamicPageState & DynamicPageActions>()(
  (set, get) => ({
    ...initialState,
    setPage: (pageSlug: string, apiBlocks: APIBlock[]) => {
      const blocksBySlug: Record<string, BlockStoreContent> = {};
      apiBlocks.map((apiBlock) => {
        blocksBySlug[apiBlock.slug] = {
          ...apiBlock,
          blockItems: [],
          numberOfColoredItems: 1,
          isLoaded: false,
        };
      });
      set({ pageSlug, isLoaded: false, blocksBySlug });
    },
    setBlockContent: (blockSlug: string, apiBlockItems: APIBlockItem[]) => {
      const { blocksBySlug } = get();
      blocksBySlug[blockSlug] = {
        ...blocksBySlug[blockSlug],
        blockItems: apiBlockItems,
      };
      if (!isBlockItemsContainAQuery(apiBlockItems)) {
        blocksBySlug[blockSlug].isLoaded = true;
      }
      if (blocksBySlug[blockSlug].display_type === "post-list-alternate") {
        blocksBySlug[blockSlug].numberOfColoredItems = apiBlockItems.filter((blockItem) =>
          blockItem.components.data.find(
            (component) => (component as ComponentData).type === TYPE_CARD
          )
        ).length;
      }

      // Sort Blocks by order
      const blocksByEntries = Object.entries(blocksBySlug).sort((a, b) => {
        if (a[1].order > b[1].order) return 1;
        if (a[1].order < b[1].order) return -1;
        return 0;
      });

      // Then update their colorIndex based on their numberOfColoredItems of the previous items
      for (
        let currentEntryIndex = 0;
        currentEntryIndex < blocksByEntries.length;
        currentEntryIndex++
      ) {
        let colorIndex = 0;
        for (
          let previousEntryIndex = 0;
          previousEntryIndex < currentEntryIndex;
          previousEntryIndex++
        ) {
          colorIndex += blocksByEntries[previousEntryIndex][1].numberOfColoredItems;
        }
        blocksBySlug[blocksByEntries[currentEntryIndex][0]].colorIndex = colorIndex;
      }
      set({ blocksBySlug });
      if (
        Object.values(blocksBySlug)
          .filter((block) => block.slug !== undefined)
          .every((block) => block.isLoaded)
      ) {
        set({ isLoaded: true });
      }
    },
    setScrollIndex: (scrollIndex: Record<string, number>) => set({ scrollIndex }),
    setBlockColorIndex: (blockSlug: string, colorIndex: number) => {
      const { blocksBySlug } = get();
      blocksBySlug[blockSlug] = {
        ...blocksBySlug[blockSlug],
        colorIndex: colorIndex,
      };
      set({ blocksBySlug });
    },
    setBlockItemComponents: (
      blockSlug: string,
      blockItemId: string,
      components: ComponentData[]
    ) => {
      const { blocksBySlug } = get();
      const blockItems: APIBlockItem[] = blocksBySlug[blockSlug]?.blockItems;
      const blockItem: APIBlockItem | undefined = blocksBySlug[blockSlug]?.blockItems.find(
        (blockItem) => blockItem.id === blockItemId
      );
      if (blockItem) {
        blockItem.components = {
          data: components,
          count: components.length,
        };
      }
      blocksBySlug[blockSlug] = {
        ...blocksBySlug[blockSlug],
        blockItems,
        isLoaded: true,
      };
      set({ blocksBySlug });
    },
    setBlockLoading: (blockSlug: string) => {
      const { blocksBySlug } = get();
      blocksBySlug[blockSlug] = {
        ...blocksBySlug[blockSlug],
        isLoaded: true,
      };
      set({ blocksBySlug });
    },
    resetPage: () => set(({ scrollIndex }) => ({ ...initialState, scrollIndex })),
  }),
  shallow
);
